const ALLOWED_KEYS = [
  "page",
  "perPage",
  "search",
  "sort",
  "datePreset",
  "isBlocked",
  "registeredFrom",
  "registeredTo",
  "lifecycleState",
  "country",
  "governorate",
  "gender",
  "category"
];

const DATE_PRESETS = new Set([
  "all",
  "today",
  "last7",
  "last30",
  "last90",
  "custom"
]);

const ALLOWED_SORTS = new Set([
  "registeredAt",
  "-registeredAt",
  "email",
  "-email",
  "fullName",
  "-fullName",
  "nameAr",
  "-nameAr",
  "nameEn",
  "-nameEn",
  "registrationNumber",
  "-registrationNumber",
  "isBlocked",
  "-isBlocked"
]);

const toPositiveInteger = (value, fallback, maximum) => {
  const number = Number.parseInt(value, 10);
  if (!Number.isFinite(number) || number < 1) return fallback;
  return maximum ? Math.min(number, maximum) : number;
};

const calendarDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
    ? match[0]
    : "";
};

const formatCalendarDate = (date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const cairoCalendarDate = (date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Africa/Cairo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

export const normalizeAccountFilters = (params) => {
  const source = params instanceof URLSearchParams ? params : new URLSearchParams(params);
  const from = calendarDate(source.get("registeredFrom"));
  const to = calendarDate(source.get("registeredTo"));
  const requestedPreset = source.get("datePreset");
  const datePreset = DATE_PRESETS.has(requestedPreset)
    ? requestedPreset
    : from || to
      ? "custom"
      : "all";
  const result = {
    page: toPositiveInteger(source.get("page"), 1),
    perPage: toPositiveInteger(source.get("perPage"), 10, 100),
    sort: ALLOWED_SORTS.has(source.get("sort"))
      ? source.get("sort")
      : "-registeredAt",
    datePreset,
    registeredFrom: datePreset === "custom" ? from : "",
    registeredTo: datePreset === "custom" ? to : ""
  };
  ALLOWED_KEYS.filter((key) => ![
    "page",
    "perPage",
    "sort",
    "datePreset",
    "registeredFrom",
    "registeredTo"
  ].includes(key)).forEach(
    (key) => {
      const value = source.get(key);
      if (value !== null && value !== "") result[key] = value;
    }
  );
  return result;
};

export const resolveAccountDateRange = (source, now = new Date()) => {
  const filters = normalizeAccountFilters(source);
  if (filters.datePreset === "custom") {
    return {
      registeredFrom: filters.registeredFrom,
      registeredTo: filters.registeredTo
    };
  }
  const days = {
    today: 1,
    last7: 7,
    last30: 30,
    last90: 90
  }[filters.datePreset];
  if (!days) return { registeredFrom: "", registeredTo: "" };
  const endValue = cairoCalendarDate(now);
  const [year, month, day] = endValue.split("-").map(Number);
  const start = new Date(Date.UTC(year, month - 1, day - (days - 1)));
  return {
    registeredFrom: formatCalendarDate(start),
    registeredTo: endValue
  };
};

export const buildAccountApiQuery = (source = {}, now = new Date()) => {
  const filters = normalizeAccountFilters(source);
  const range = resolveAccountDateRange(filters, now);
  const params = buildAccountQuery({ ...filters, ...range });
  params.delete("datePreset");
  return params;
};

export const hasActiveAccountFilters = (filters = {}) => Boolean(
  String(filters.search || "").trim() ||
  (filters.lifecycleState && filters.lifecycleState !== "ACTIVE") ||
  filters.country ||
  filters.governorate ||
  filters.gender ||
  filters.category ||
  (filters.datePreset && filters.datePreset !== "all") ||
  (filters.sort && filters.sort !== "-registeredAt")
);

export const buildAccountQuery = (filters = {}) => {
  const params = new URLSearchParams();
  ALLOWED_KEYS.forEach((key) => {
    let value = filters[key];
    if (typeof value === "string") value = value.trim();
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  return params;
};

export const updateUrlFilters = (current, patch) => {
  const next = { ...normalizeAccountFilters(current), ...patch };
  return buildAccountQuery(next);
};

export { ALLOWED_KEYS, ALLOWED_SORTS, DATE_PRESETS };
